import React, { useEffect, useState } from 'react';
import { Card, Title, Text, Table, TableHead, TableRow, TableHeaderCell, TableBody, TableCell, Badge, Flex, Button } from '@tremor/react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { adminApi } from '../services/api';
import './AdminDashboards.css';

// Admin-only overview of the system's data (Ohad, 2026-07-28): every dashboard we
// run, every table we write, and every source we read — with live row counts. Built
// to be shown to Shachar, who owns the data layer and until now could only learn
// what we keep and what we read straight out of DWH by reading our source.
//
// The activity log lives at the top of it: last week by default, a month if asked,
// and one click on a person to see only them.

interface TableInfo {
  dataset: string; table: string; purpose: string; dashboards: string[];
  ours: boolean; kind: string | null; rows: number | null;
  size_mb: number | null; last_modified: string | null;
  bypass: boolean; used: boolean; ask: string | null; documented: boolean;
  expects: { dataset: string; table: string; columns: string[] } | null;
  status: string; status_actor: string | null; status_note: string | null;
  check: { ok: boolean; exists: boolean; missing: string[]; target: string } | null;
}
interface Catalog { writes: TableInfo[]; reads: TableInfo[] }
interface UserActivity {
  email: string | null; role: string | null; logins: number; page_views: number;
  edits: number; minutes: number; dashboards: number; last_seen: string | null;
}
interface DashboardActivity {
  dashboard: string | null; viewers: number; views: number; minutes: number; median_seconds: number;
}
interface ActivityEvent {
  ts: string; email: string | null; event: string; dashboard: string | null;
  path: string | null; method: string | null; duration_seconds: number | null;
  status_code: number | null; detail: string | null;
}
interface ActivityView {
  period: string; period_label: string; env: string; by_user: UserActivity[];
  by_dashboard: DashboardActivity[]; events: ActivityEvent[]; total_events: number;
}

// Last 6 months, so "a specific month" is a pick and not a date field to type into.
const monthOptions = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
};

const EVENT_LABEL: Record<string, string> = {
  login: 'התחברות', page_view: 'צפייה', edit: 'שינוי',
};
const EVENT_COLOR: Record<string, string> = {
  login: 'gray', page_view: 'blue', edit: 'amber',
};
interface Holder { email: string; level: string; enabled: boolean }
interface DashboardRow {
  id: string; name: string; route: string; purpose: string;
  metrics: number; holders: number; emails: string[];
  admins: number; admin_emails: string[]; holders_detail: Holder[];
}
interface AdminUser { id: string; email: string; name: string; role: string; enabled?: boolean }

const LEVEL_LABEL: Record<string, string> = { view: 'צפייה', edit: 'עריכה' };

const num = (n: number | null) => (n === null ? '—' : n.toLocaleString('en-US'));

// Deep link straight into the BigQuery console, on the table itself. Needs Google
// Cloud access to the project — for anyone without it the link simply refuses.
const bqLink = (dataset: string, table: string) =>
  `https://console.cloud.google.com/bigquery?project=taliazdwh&ws=!1m5!1m4!4m3!1staliazdwh!2s${dataset}!3s${table}`;

// One request: the ask, who marked it done, what the automatic check found, and the
// two buttons. They are deliberately separate — Shachar says "built it", the system
// checks it against BigQuery, and only then does Ohad accept it. Neither button
// changes code; the second one is the signal that the code may now be changed.
function RequestCell({ t, onChanged }: { t: TableInfo; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const id = `${t.dataset}.${t.table}`;
  const move = async (status: string) => {
    setBusy(true);
    try {
      await adminApi.setRequestStatus(id, status);
      onChanged();
    } finally { setBusy(false); }
  };
  const c = t.check;
  return (
    <div className="space-y-1.5">
      <div className={t.bypass ? 'text-red-700' : 'text-amber-700'}>{t.ask}</div>
      {t.expects && (
        <div className="text-gray-500">
          נבדק מול: <span className="admin-ltr">{t.expects.dataset}.{t.expects.table}</span>
          {' · '}{t.expects.columns.length} עמודות נדרשות
        </div>
      )}
      {t.status === 'done' && c && (
        c.ok
          ? <div className="text-green-700">✓ נבנה ואומת — כל העמודות הנדרשות קיימות</div>
          : <div className="text-red-700">
              ✗ סומן כבוצע אבל הבדיקה נכשלה — {c.exists
                ? <>חסרות: <span className="admin-ltr">{c.missing.join(', ')}</span></>
                : 'הטבלה לא קיימת'}
            </div>
      )}
      {t.status === 'verified' && <div className="text-green-700">✓✓ אושר — אפשר לעבור אליו בקוד</div>}
      {t.status_actor && (
        <div className="text-gray-500">
          סומן בידי <span className="admin-ltr">{t.status_actor}</span>
        </div>
      )}
      <div className="flex gap-2 pt-0.5">
        {t.status === 'open' && (
          <Button size="xs" variant="secondary" loading={busy} onClick={() => move('done')}>
            שחר: בניתי, בדוק
          </Button>
        )}
        {t.status === 'done' && (
          <>
            <Button size="xs" variant="primary" loading={busy} disabled={!c?.ok}
              title={c?.ok ? 'מאשר שאפשר להעביר את הקוד למקור החדש' : 'הבדיקה עדיין נכשלת'}
              onClick={() => move('verified')}>
              אוהד: אשר
            </Button>
            <Button size="xs" variant="light" loading={busy} onClick={() => move('open')}>
              החזר לפתוח
            </Button>
          </>
        )}
        {t.status === 'verified' && (
          <Button size="xs" variant="light" loading={busy} onClick={() => move('open')}>
            פתח מחדש
          </Button>
        )}
      </div>
    </div>
  );
}

function TableList({ rows, emptyNote, showAsk, onChanged }: { rows: TableInfo[]; emptyNote: string; showAsk?: boolean; onChanged?: () => void }) {
  if (!rows.length) return <Text className="text-gray-500 mt-2">{emptyNote}</Text>;
  return (
    <Table className="mt-3">
      <TableHead>
        <TableRow>
          <TableHeaderCell>טבלה</TableHeaderCell>
          <TableHeaderCell>סוג</TableHeaderCell>
          <TableHeaderCell>למה היא קיימת</TableHeaderCell>
          <TableHeaderCell>דשבורדים</TableHeaderCell>
          {showAsk && <TableHeaderCell>מה צריך משחר</TableHeaderCell>}
          <TableHeaderCell className="admin-num">שורות</TableHeaderCell>
          <TableHeaderCell className="admin-num">עודכן</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((t) => (
          <TableRow key={`${t.dataset}.${t.table}`}>
            <TableCell>
              <span className="font-medium text-gray-800 admin-ltr">{t.dataset}.{t.table}</span>
              <a className="admin-bq-link" href={bqLink(t.dataset, t.table)}
                target="_blank" rel="noreferrer"
                title="פתח את הטבלה בקונסולת BigQuery">↗ BigQuery</a>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                <Badge color={t.kind === 'VIEW' ? 'gray' : 'blue'} size="xs">
                  {t.kind === 'VIEW' ? 'VIEW' : 'טבלה'}
                </Badge>
                {t.bypass && (
                  <Badge color="red" size="xs" title="נקרא ישירות מ-DWH או מהמקור הישן, לא דרך dash_views">
                    עוקף dash_views
                  </Badge>
                )}
                {!t.used && (
                  <Badge color="amber" size="xs" title="קיים עבורנו, אבל שום דשבורד לא קורא אותו כרגע">
                    לא בשימוש
                  </Badge>
                )}
                {!t.documented && (
                  <Badge color="red" size="xs" title="הקוד נוגע בטבלה הזו ואיש לא תיעד אותה — התגלתה אוטומטית">
                    לא מתועד
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="max-w-lg whitespace-normal text-gray-600 text-xs leading-relaxed">
              {t.purpose}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {t.dashboards.map((d) => <Badge key={d} size="xs" color="indigo">{d}</Badge>)}
              </div>
            </TableCell>
            {showAsk && (
              <TableCell className="max-w-sm whitespace-normal text-xs leading-relaxed">
                {t.ask
                  ? <RequestCell t={t} onChanged={onChanged!} />
                  : <span className="text-gray-500">—</span>}
              </TableCell>
            )}
            <TableCell className="admin-num tabular-nums">
              {t.kind === 'VIEW' ? <span className="text-gray-500" title="VIEW — נגזרת, אין לה שורות משלה">—</span> : num(t.rows)}
            </TableCell>
            <TableCell className="admin-num text-xs text-gray-500">{t.last_modified || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Who holds one dashboard, and the two things you actually want to do about it.
// The users screen answers "what does Lee see"; this answers "who sees the salary
// dashboard" — the question you ask before releasing one, and the one that leaves a
// stale grant alive when nobody asks it.
function Holders({ panel, users, onChanged }: {
  panel: DashboardRow; users: AdminUser[]; onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState('');
  const [addLevel, setAddLevel] = useState('view');
  const [err, setErr] = useState<string | null>(null);

  const write = async (email: string, level: string) => {
    setBusy(email); setErr(null);
    try {
      await adminApi.setGrants({ email, grants: { [panel.id]: level } });
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'הפעולה נכשלה');
    } finally { setBusy(null); }
  };

  const remove = (h: Holder) => {
    // Revocation is immediate. The confirmation names both sides, because the row
    // you meant and the row you clicked are not always the same row.
    if (!window.confirm(`לשלול מ-${h.email} את הגישה ל"${panel.name}"?\n\nהשינוי נכנס לתוקף מיד.`)) return;
    write(h.email, 'none');
  };

  const held = new Set(panel.holders_detail.map((h) => h.email));
  const candidates = users.filter(
    (u) => u.enabled !== false && u.role !== 'admin' && !held.has(u.email));

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      {err && <Text className="text-red-600 text-xs">{err}</Text>}
      {panel.holders_detail.length === 0 && (
        <Text className="text-xs text-gray-500">אין מחזיקים בהרשאה מפורשת.</Text>
      )}
      {panel.holders_detail.map((h) => (
        <Flex key={h.email} justifyContent="between" alignItems="center"
          className="bg-white rounded border border-gray-200 px-3 py-1.5">
          <div className="flex items-center gap-2">
            <span className="text-sm admin-ltr">{h.email}</span>
            <Badge size="xs" color={h.level === 'edit' ? 'amber' : 'blue'}>
              {LEVEL_LABEL[h.level] || h.level}
            </Badge>
            {h.enabled === false && <Badge size="xs" color="gray">מושבת</Badge>}
          </div>
          <Flex className="gap-1 w-auto">
            <Button size="xs" variant="light" loading={busy === h.email}
              onClick={() => write(h.email, h.level === 'edit' ? 'view' : 'edit')}
              title="החלף בין צפייה לעריכה">
              {h.level === 'edit' ? '→ צפייה' : '→ עריכה'}
            </Button>
            <Button size="xs" variant="light" color="red" loading={busy === h.email}
              onClick={() => remove(h)}>הסר</Button>
          </Flex>
        </Flex>
      ))}

      {panel.admin_emails.length > 0 && (
        <Text className="text-xs text-gray-500">
          ובנוסף {panel.admins} אדמינים — <span className="admin-ltr">{panel.admin_emails.join(', ')}</span> —
          רואים אותו מתוקף התפקיד, בלי שורת הרשאה שאפשר להסיר.
        </Text>
      )}

      <Flex justifyContent="start" className="gap-2 pt-1">
        <select value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
          <option value="">הוסף משתמש…</option>
          {candidates.map((u) => <option key={u.email} value={u.email}>{u.email}</option>)}
        </select>
        <select value={addLevel} onChange={(e) => setAddLevel(e.target.value)}
          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
          <option value="view">צפייה</option>
          <option value="edit">עריכה</option>
        </select>
        <Button size="xs" disabled={!addEmail} loading={busy === addEmail}
          onClick={() => { write(addEmail, addLevel); setAddEmail(''); }}>הוסף</Button>
      </Flex>
    </div>
  );
}

export default function AdminDashboards() {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [panels, setPanels] = useState<DashboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('7d');
  const [who, setWho] = useState<string | null>(null);      // null = everyone
  const [activity, setActivity] = useState<ActivityView | null>(null);
  const [loadingAct, setLoadingAct] = useState(true);
  const [showFeed, setShowFeed] = useState(false);
  const [openHolders, setOpenHolders] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [env, setEnv] = useState('all');   // dev and prod share one table

  const loadCatalog = () => {
    adminApi.getCatalog().then((r) => setCatalog(r.data as Catalog))
      .catch(() => setError('לא ניתן לטעון את הקטלוג'));
  };

  const loadPanels = () =>
    adminApi.getDashboardsOverview().then((r) => setPanels(r.data as DashboardRow[])).catch(() => {});

  useEffect(() => {
    loadCatalog();
    loadPanels();
    // Only for the "add a holder" picker — the same roster the users screen shows.
    adminApi.listUsers().then((r) => setUsers(r.data as AdminUser[])).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setLoadingAct(true);
    adminApi.getActivity(period, who || undefined, env)
      .then((r) => { if (alive) setActivity(r.data as ActivityView); })
      .catch(() => { if (alive) setActivity(null); })
      .finally(() => { if (alive) setLoadingAct(false); });
    return () => { alive = false; };
  }, [period, who, env]);

  return (
    // dir stays OFF the shell: the sidebar places itself with `order-last`, so an
    // RTL wrapper here flips the whole layout and lands it on the wrong edge. Every
    // other page does the same — direction belongs to the content.
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="ניהול דשבורדים ונתונים" />
        <main className="flex-1 overflow-y-auto p-6 space-y-6 admin-rtl">
          {error && <Text className="text-red-600">{error}</Text>}

          {/* The log. Default is the last week — the question is almost always
              "what happened lately", and a month of rows buries it. */}
          <Card>
            <Flex justifyContent="between" alignItems="center" className="flex-wrap gap-3">
              <div>
                <Title>לוג פעילות</Title>
                <Text className="mt-1">
                  מי נכנס, מה פתח, כמה זמן שהה ומה שינה · {activity?.period_label}
                  {activity ? ` · ${activity.total_events.toLocaleString('en-US')} רשומות` : ''}
                  {env === 'all' && <span className="text-amber-700"> · כולל תנועת dev</span>}
                </Text>
              </div>
              <Flex className="gap-2 w-auto flex-wrap">
                {[{ id: '7d', label: 'שבוע אחרון' }, { id: '30d', label: '30 ימים' }].map((p) => (
                  <Button key={p.id} size="xs" variant={period === p.id ? 'primary' : 'secondary'}
                    onClick={() => setPeriod(p.id)}>
                    {p.label}
                  </Button>
                ))}
                <select value={period.includes('-') ? period : ''}
                  onChange={(e) => e.target.value && setPeriod(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white">
                  <option value="">חודש ספציפי…</option>
                  {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={env} onChange={(e) => setEnv(e.target.value)}
                  title="dev ופרוד כותבים לאותה טבלה — בלי הפרדה, הבדיקות שלנו נספרות כשימוש אמיתי"
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white">
                  <option value="all">הכל</option>
                  <option value="prod">פרוד בלבד</option>
                  <option value="dev">dev בלבד</option>
                </select>
                {who && (
                  <Button size="xs" variant="light" onClick={() => setWho(null)}>
                    {who} ✕
                  </Button>
                )}
              </Flex>
            </Flex>

            {loadingAct && <Text className="mt-4 text-gray-500">טוען…</Text>}
            {!loadingAct && activity && !activity.total_events && (
              <Text className="mt-4 text-gray-500">
                אין פעילות בתקופה הזו. הלוג מתחיל להתמלא מרגע שהגרסה עלתה — קח יום-יומיים.
              </Text>
            )}

            {!loadingAct && !!activity?.total_events && (
              <>
                <Text className="mt-5 font-medium text-gray-700">
                  לפי משתמש <span className="text-xs font-normal text-gray-500">· לחיצה על שורה מסננת אליו</span>
                </Text>
                <Table className="mt-2">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>משתמש</TableHeaderCell>
                      <TableHeaderCell className="admin-num">התחברויות</TableHeaderCell>
                      <TableHeaderCell className="admin-num">צפיות</TableHeaderCell>
                      <TableHeaderCell className="admin-num">דשבורדים</TableHeaderCell>
                      <TableHeaderCell className="admin-num">דקות</TableHeaderCell>
                      <TableHeaderCell className="admin-num">שינויים</TableHeaderCell>
                      <TableHeaderCell className="admin-num">נראה לאחרונה</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.by_user.map((u) => (
                      <TableRow key={u.email || 'anon'}
                        className={`cursor-pointer hover:bg-gray-50 ${who === u.email ? 'bg-blue-50' : ''}`}
                        onClick={() => setWho(who === u.email ? null : u.email)}>
                        <TableCell>
                          <span className="font-medium text-gray-800 admin-ltr">{u.email || '—'}</span>
                          {u.role && (
                            <span className="text-xs text-gray-500 admin-ltr" style={{ marginInlineStart: 8 }}>
                              {u.role}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="admin-num tabular-nums">{u.logins}</TableCell>
                        <TableCell className="admin-num tabular-nums">{u.page_views}</TableCell>
                        <TableCell className="admin-num tabular-nums">{u.dashboards}</TableCell>
                        <TableCell className="admin-num tabular-nums">{u.minutes.toFixed(2)}</TableCell>
                        <TableCell className="admin-num tabular-nums">
                          {u.edits ? <Badge color="amber" size="xs">{u.edits}</Badge> : '—'}
                        </TableCell>
                        <TableCell className="admin-num text-xs text-gray-500">{u.last_seen || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Text className="mt-6 font-medium text-gray-700">לפי דשבורד</Text>
                <Table className="mt-2">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>דשבורד</TableHeaderCell>
                      <TableHeaderCell className="admin-num">צופים</TableHeaderCell>
                      <TableHeaderCell className="admin-num">צפיות</TableHeaderCell>
                      <TableHeaderCell className="admin-num">דקות</TableHeaderCell>
                      <TableHeaderCell className="admin-num">חציון שהייה</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.by_dashboard.map((d) => (
                      <TableRow key={d.dashboard || '—'}>
                        <TableCell className="font-medium text-gray-800">{d.dashboard}</TableCell>
                        <TableCell className="admin-num tabular-nums">{d.viewers}</TableCell>
                        <TableCell className="admin-num tabular-nums">{d.views}</TableCell>
                        <TableCell className="admin-num tabular-nums">{d.minutes.toFixed(2)}</TableCell>
                        <TableCell className="admin-num tabular-nums">{d.median_seconds.toFixed(2)}ש׳</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Flex justifyContent="start" className="mt-6 gap-2">
                  <Button size="xs" variant="light" onClick={() => setShowFeed(!showFeed)}>
                    {showFeed ? 'הסתר פירוט' : `פירוט אירועים (${activity.events.length})`}
                  </Button>
                </Flex>
                {showFeed && (
                  <Table className="mt-2">
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>זמן</TableHeaderCell>
                        <TableHeaderCell>משתמש</TableHeaderCell>
                        <TableHeaderCell>סוג</TableHeaderCell>
                        <TableHeaderCell>דשבורד</TableHeaderCell>
                        <TableHeaderCell>מה</TableHeaderCell>
                        <TableHeaderCell className="admin-num">שהייה</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activity.events.map((e, i) => (
                        <TableRow key={`${e.ts}-${i}`}>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap"><span className="admin-ltr">{e.ts}</span></TableCell>
                          <TableCell className="text-xs"><span className="admin-ltr">{e.email || '—'}</span></TableCell>
                          <TableCell>
                            <Badge size="xs" color={EVENT_COLOR[e.event] as any}>
                              {EVENT_LABEL[e.event] || e.event}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">{e.dashboard || '—'}</TableCell>
                          <TableCell className="text-xs text-gray-600 max-w-md whitespace-normal">
                            {e.event === 'edit'
                              ? <>
                                  <span className="font-mono admin-ltr">{e.method} {e.path}</span>
                                  {e.status_code && e.status_code >= 400 &&
                                    <Badge color="red" size="xs" className="mr-2">{e.status_code}</Badge>}
                                  {e.detail && <div className="text-gray-500 admin-ltr">{e.detail}</div>}
                                </>
                              : <span className="font-mono admin-ltr">{e.path || '—'}</span>}
                          </TableCell>
                          <TableCell className="admin-num tabular-nums text-xs">
                            {e.duration_seconds ? `${e.duration_seconds.toFixed(2)}ש׳` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </Card>

          <Card>
            <Title>הדשבורדים</Title>
            <Text className="mt-1">
              כל לוח שקיים במערכת, מה הוא עונה עליו, וכמה אנשים מחזיקים בו הרשאה.
              לוח בלי מחזיקים הוא לוח שאיש לא רואה.
            </Text>
            <Table className="mt-3">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>דשבורד</TableHeaderCell>
                  <TableHeaderCell>נתיב</TableHeaderCell>
                  <TableHeaderCell>מטרה</TableHeaderCell>
                  <TableHeaderCell className="admin-num">מחזיקים</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {panels.map((p) => (
                  <React.Fragment key={p.id}>
                  <TableRow>
                    <TableCell>
                      <span className="font-medium text-gray-800">{p.name}</span>
                      <span className="text-xs text-gray-500 admin-ltr" style={{ marginInlineStart: 8 }}>
                        {p.id}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500"><span className="admin-ltr">{p.route}</span></TableCell>
                    <TableCell className="max-w-lg whitespace-normal text-gray-600 text-xs leading-relaxed">
                      {p.purpose}
                    </TableCell>
                    <TableCell className="admin-num">
                      <button onClick={() => setOpenHolders(openHolders === p.id ? null : p.id)}
                        title="הצג את המחזיקים, והוסף או הסר">
                        <Badge color={p.holders ? 'green' : 'gray'} size="xs">
                          {p.holders} {openHolders === p.id ? '▲' : '▼'}
                        </Badge>
                      </button>
                      <span className="text-xs text-gray-500" style={{ marginInlineStart: 6 }}
                        title={`אדמינים — רואים הכל בלי הרשאה מפורשת:\n${p.admin_emails.join('\n')}`}>
                        +{p.admins} אדמין
                      </span>
                    </TableCell>
                  </TableRow>
                  {openHolders === p.id && (
                    <TableRow>
                      <TableCell colSpan={4} className="whitespace-normal">
                        <Holders panel={p} users={users} onChanged={loadPanels} />
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card>
            <Flex justifyContent="between" alignItems="start">
              <div>
                <Title>מה אנחנו כותבים</Title>
                <Text className="mt-1">
                  הטבלאות שהדשבורדים כותבים אליהן, בשני דאטהסטים משלנו. כולן
                  append-only — שורה חדשה לכל שינוי, כך שההיסטוריה אומרת מי שינה מה ומתי,
                  ואף שורה לא נדרסת. שום כתיבה שלנו לא נוגעת בנתונים הקליניים.
                </Text>
              </div>
            </Flex>
            <TableList rows={catalog?.writes || []} emptyNote="טוען…" />
          </Card>

          <Card>
            <Title>מה אנחנו קוראים</Title>
            <Text className="mt-1">
              המקורות שהדשבורדים נשענים עליהם. מה שמסומן <b>עוקף dash_views</b> נקרא ישירות
              מ-DWH או מהמקור הישן, ומה שמסומן <b>לא בשימוש</b> נבנה עבורנו אבל שום דשבורד
              לא קורא אותו כרגע — ועל שני אלה בדיוק נשמח להערות.
            </Text>
            <TableList rows={catalog?.reads || []} emptyNote="טוען…" showAsk onChanged={loadCatalog} />
          </Card>
        </main>
      </div>
    </div>
  );
}
